import { createBrowserRouter, RouterProvider, Outlet } from 'react-router-dom';
import { Toaster } from 'react-hot-toast';

import { ROUTES } from './constants';
import { useScrollToTop } from './hooks';

// Layout
import { Main } from './pages/Main/Main';

// Public pages
import { Home } from './pages/Home/Home';
import { NotFound } from './pages/NotFound/NotFound';

// Protected pages
import { Profile } from './pages/Profile/Profile';
import { ProtectedRoute } from './components/features/ProtectedRoute/ProtectedRoute';
import { RoleProtectedRoute } from './components/features/RoleProtectedRoute/RoleProtectedRoute';

// Auth pages
import { Login } from './pages/auth/Login/Login';
import { Register } from './pages/auth/Register/Register';
import { VerifyEmail } from './pages/auth/VerifyEmail/VerifyEmail';
import { ForgotPassword } from './pages/auth/ForgotPassword/ForgotPassword';

// Dashboard pages
import { DashboardLayout } from './pages/Dashboard/DashboardLayout';
import { DashboardIndex } from './pages/Dashboard/DashboardIndex';
import { Providers } from './pages/Dashboard/Providers';
import { ProviderDetails } from './pages/Dashboard/ProviderDetails';
import { AddEditProvider } from './pages/Dashboard/AddEditProvider';
import { Services } from './pages/Dashboard/Services';
import { Categories } from './pages/Dashboard/Categories';
import { Attributes } from './pages/Dashboard/Attributes';
import { Pricing } from '@/pages/Dashboard/Pricing';
import { TokenUsage } from '@/pages/Dashboard/TokenUsage';
import { ProviderStats } from '@/pages/Dashboard/ProviderStats';
import { Chat } from '@/pages/Dashboard/Chat';
import { GenerateInvitation } from '@/pages/Dashboard/GenerateInvitation';
import { GeneratePresentation } from '@/pages/Dashboard/GeneratePresentation';
import { InvitationsList } from '@/pages/Dashboard/InvitationsList';
import { InvitationDetail } from '@/pages/Dashboard/InvitationDetail';
import { PresentationsList } from '@/pages/Dashboard/PresentationsList';
import { PresentationDetail } from '@/pages/Dashboard/PresentationDetail';
import { PresentationEdit } from '@/pages/Dashboard/PresentationEdit';
import { GenerateRenders } from '@/pages/Dashboard/GenerateRenders';
import { RendersList } from '@/pages/Dashboard/RendersList';
import { RenderDetail } from '@/pages/Dashboard/RenderDetail';
import { Users } from '@/pages/Dashboard/Users';
import { AdminActions } from '@/pages/Dashboard/AdminActions';
import { Notifications } from '@/pages/Dashboard/Notifications';
import { Waiting } from '@/pages/Dashboard/Waiting/Waiting';
import { Deals } from '@/pages/Dashboard/Deals/Deals';
import { DealDetail } from '@/pages/Dashboard/Deals/DealDetail';

// Legal pages (public)
import { Privacy } from './pages/Legal/Privacy';
import { Terms } from './pages/Legal/Terms';

// External pages (no auth)
import { ExternalProviderSubmit } from './pages/ExternalProviderSubmit/ExternalProviderSubmit';

import { ModalBase } from './components/modals/ModalBase/ModalBase';

/** Root layout — renders global UI (toaster, modal) plus child routes. */
function RootLayout() {
  useScrollToTop();

  return (
    <div>
      <Toaster position="top-center" />
      <ModalBase />
      <Outlet />
    </div>
  );
}

const router = createBrowserRouter([
  {
    element: <RootLayout />,
    children: [
      // All dashboard routes — any authenticated user (including unassigned)
      {
        element: <RoleProtectedRoute allowedRoles={['admin', 'staff', 'provider', 'unassigned']} />,
        children: [
          {
            path: ROUTES.dashboard,
            element: <DashboardLayout />,
            children: [
              // Unassigned users — waiting page only
              { path: ROUTES.dashboardWaiting, element: <Waiting /> },

              // Admin-only routes
              {
                element: <RoleProtectedRoute allowedRoles={['admin']} />,
                children: [
                  { path: ROUTES.dashboardAdminActions, element: <AdminActions /> },
                  { path: ROUTES.dashboardNotifications, element: <Notifications /> },
                ],
              },
              // Admin + Staff routes
              {
                element: <RoleProtectedRoute allowedRoles={['admin', 'staff']} />,
                children: [
                  { path: ROUTES.dashboardUsers, element: <Users /> },
                  { path: ROUTES.dashboardProvidersNew, element: <AddEditProvider /> },
                  { path: ROUTES.dashboardServices, element: <Services /> },
                  { path: ROUTES.dashboardCategories, element: <Categories /> },
                  { path: ROUTES.dashboardAttributes, element: <Attributes /> },
                  { path: ROUTES.dashboardPricing, element: <Pricing /> },
                  { path: ROUTES.dashboardTokenUsage, element: <TokenUsage /> },
                  { path: ROUTES.dashboardProviderStats, element: <ProviderStats /> },
                  { path: ROUTES.dashboardChat, element: <Chat /> },
                  { path: ROUTES.dashboardChatSession, element: <Chat /> },
                  { path: ROUTES.dashboardGenerateInvitation, element: <GenerateInvitation /> },
                  { path: ROUTES.dashboardGeneratePresentation, element: <GeneratePresentation /> },
                  { path: ROUTES.dashboardInvitations, element: <InvitationsList /> },
                  { path: ROUTES.dashboardInvitationDetail, element: <InvitationDetail /> },
                  { path: ROUTES.dashboardInvitationEdit, element: <GenerateInvitation /> },
                  { path: ROUTES.dashboardPresentations, element: <PresentationsList /> },
                  { path: ROUTES.dashboardPresentationDetail, element: <PresentationDetail /> },
                  { path: ROUTES.dashboardPresentationEdit, element: <PresentationEdit /> },
                  { path: ROUTES.dashboardGenerateRenders, element: <GenerateRenders /> },
                  { path: ROUTES.dashboardRenders, element: <RendersList /> },
                  { path: ROUTES.dashboardRenderDetail, element: <RenderDetail /> },
                  { path: ROUTES.dashboardDeals, element: <Deals /> },
                  { path: ROUTES.dashboardDealDetail, element: <DealDetail /> },
                ],
              },
              // Admin + Staff + Provider routes
              {
                element: <RoleProtectedRoute allowedRoles={['admin', 'staff', 'provider']} />,
                children: [
                  { path: ROUTES.profile, element: <Profile /> },
                  { path: ROUTES.dashboardProviders, element: <Providers /> },
                  { path: ROUTES.dashboardProviderDetails, element: <ProviderDetails /> },
                  { path: ROUTES.dashboardProvidersEdit, element: <AddEditProvider /> },
                ],
              },
              // Dashboard index — redirects based on role
              { index: true, element: <DashboardIndex /> },
            ],
          },
        ],
      },
      {
        path: ROUTES.main,
        element: <Main />,
        children: [
          { path: ROUTES.home, element: <Home /> },
          { path: ROUTES.login, element: <Login /> },
          { path: ROUTES.register, element: <Register /> },
          { path: ROUTES.verifyEmail, element: <VerifyEmail /> },
          { path: ROUTES.forgotPassword, element: <ForgotPassword /> },
          {
            element: <ProtectedRoute />,
            children: [],
          },
        ],
      },
      { path: ROUTES.privacy, element: <Privacy /> },
      { path: ROUTES.terms, element: <Terms /> },
      { path: ROUTES.externalProviderSubmit, element: <ExternalProviderSubmit /> },
      { path: ROUTES.notFound, element: <NotFound /> },
    ],
  },
]);

function App() {
  return <RouterProvider router={router} />;
}

export { App };
